// Not window.open(url, '_blank', 'noopener'): with that feature it always
// returns null, so a blocked tab and an opened one look the same. Clearing
// `opener` afterwards gives the same protection as rel="noopener".
const openNewTab = (url) => {
  const tab = window.open(url, '_blank');

  if (tab) {
    tab.opener = null;
  }

  return tab;
};

const closeTab = (tab) => {
  if (tab && !tab.closed) {
    tab.close();
  }
};

/**
 * SW2-2454 — open a new tab for a URL that is only known after async work.
 *
 * Mobile Safari's pop-up blocker only lets a page open a tab while it is still
 * handling the user's tap. A `window.open` (or a `target="_blank"` link click)
 * after an `await` is treated as an unrequested pop-up and silently dropped.
 * So the tab is opened blank during the tap, and sent to the URL once the URL
 * is known.
 *
 * Call it synchronously from the click handler, before any `await`.
 *
 * @param {() => Promise<string | null>} resolveUrl async work that yields the
 *   URL, or null when there turns out to be nothing to open
 * @returns {Promise<{ url: string | null, opened: boolean }>} the URL, and
 *   whether a tab is on its way to it. When there is a URL but `opened` is
 *   false, every attempt was blocked and the caller decides whether to fall
 *   back to this tab. The blank tab is closed if there is no URL or the work
 *   fails.
 */
export const openTabAfter = (resolveUrl) => {
  const tab = openNewTab('');

  return Promise.resolve()
    .then(resolveUrl)
    .then((url) => {
      if (!url) {
        closeTab(tab);

        return { url: null, opened: false };
      }

      if (tab && !tab.closed) {
        tab.location.href = url;

        return { url, opened: true };
      }

      // The blank tab was blocked or closed. Desktop browsers still allow a
      // direct open shortly after a click.
      return { url, opened: Boolean(openNewTab(url)) };
    })
    .catch((err) => {
      closeTab(tab);

      throw err;
    });
};

export default openTabAfter;
